import React, { FC, useEffect, useState } from "react"
import Breadcrumb from "../../../components/common/Breadcrumbs";
import { useFindUserSettingsByGroupNameQuery } from "../../../components/common/userSetting/userSettingsApi";
import { createClickableLink } from "../../../components/common/datatable/DataTableUtils";
import { SearchInputWrapper } from "../../../components/common/datatable/filter/SearchInputWrapper";
import { DataTable } from "../../../components/common/datatable/DataTable";
import { DataTableColumn } from "../../../components/common/datatable/DataTableTypes";
import { useSearchHighRiskAccountsQuery, useVerifyHighRiskAccountByIdMutation } from "./highRiskAccountApi";
import { formatAsCurrency } from "../../../helpers/utils/TextUtils";
import { hasPermission } from "../../../helpers/utils/AccessControlUtils";
import { showToast } from "../../../helpers/utils/Utils";
import AddLinkedAccountModal from "./modal/AddLinkedAccount";
import MarkFalsePositiveModal from "./modal/MarkFalsePositiveModal";
import CollectorSearchInputWrapper from "../../../components/common/datatable/filter/CollectorSearchInputWrapper";


const HighRiskAccount: FC = () => {

    const [highRiskAccountId, setHighRiskAccountId] = useState("");
    const [showFalsePositiveConfirm, setShowFalsePositiveConfirm] = useState(false);
    const [taxAccountNumber, setTaxAccountNumber] = useState('');
    const [showAddLinkedAccountModal, setShowAddLinkedAccountModal] = useState(false);
    const [highRiskAccountObj, setHighRiskAccountObj] = useState(null);
    const [collectorId, setCollectorId] = useState('');
    const [note, setNote] = useState('');

    const [verifyHighRiskAccountById] = useVerifyHighRiskAccountByIdMutation();

    const [crumbs] = useState([
        { content: "Home", url: "/home" },
        { content: "High Risk Account", url: "" },

    ])

    const handleHighRiskAccountFalsePositive = async (e: any) => {

        const commonObj = {
            collectorId: collectorId,
            taxAccountNumber: taxAccountNumber,
            note: note // Ensure `note` is passed dynamically
        };
        if (highRiskAccountId) {
            try {
                const response = await verifyHighRiskAccountById({ highRiskAccountId: highRiskAccountId, requestBody: commonObj }).unwrap();
                if (response?.errorCode === 200) {
                    showToast("High Risk Account Verified successfully!");
                    setShowFalsePositiveConfirm(false);
                } else {
                    // responseDTOExceptionHandler(response);
                }
            } catch (error) {
                console.error("Verification failed:", error);
            }
        }
    };

    useEffect(() => {
        document.title = "High Risk Account";
    });

    const { data: userSettings } = useFindUserSettingsByGroupNameQuery("High Risk Account");

    const columns: DataTableColumn[] = [
        {
            label: "Collector", accessor: "collectorName", visibility: { hasShowSetting: false },
            canSort: false,
            cell: (props: any) => {
                let path = `/collectors/${props.row.original.collectorId}`;
                return createClickableLink(path, props.value, userSettings, false, "", ["COLL-R"]);
            },
            filterOptions: [{
                filterComponent: <CollectorSearchInputWrapper accessor="collectorId" />,
                overrideFilterAccessor: "collectorId",
                overrideFilterLabel: "Collector",
            }]
        },
        {
            label: "Tax Account No.", accessor: "taxAccountNumber",
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Enter Tax Account No..."}
                    name={"taxAccountNumber"} accessor={"taxAccountNumber"} />,
            }]
        },

        {
            label: "Appraisal District No.", accessor: "appraisalDistrictNumber",
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Enter Appraisal District No..."}
                    name={"appraisalDistrictNumber"}
                    accessor={"appraisalDistrictNumber"} />,
            }]
        },

        {
            label: "Estimated Tax Due", accessor: "estimatedTaxDue",
            cell: (props: { value: any; }) => <span>{formatAsCurrency(props.value)}</span>,
        },
        {
            label: "Translated Account No.",
            accessor: "translatedAccountNumber",
            visibility: { showByDefault: false },
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Enter Translated Account No..."}
                    name={"translatedAccountNumber"}
                    accessor={"translatedAccountNumber"} />,
            }]
        },
        {
            label: "Translated Appraisal No.",
            accessor: "translatedAppraisalNumber",
            visibility: { showByDefault: false },
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Enter Translated Appraisal No..."}
                    name={"translatedAppraisalNumber"}
                    accessor={"translatedAppraisalNumber"} />,
            }]
        },
        {
            label: "First Year Delinquent", accessor: "firstYearDelinquent",
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"number"} placeholder={"Enter First Year Delinquent..."}
                    name={"firstYearDelinquent"}
                    accessor={"firstYearDelinquent"} />,
            }]
        },
        {
            label: "Last Year Delinquent", accessor: "lastYearDelinquent",
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"number"} placeholder={"Enter Last Year Delinquent..."}
                    name={"lastYearDelinquent"} accessor={"lastYearDelinquent"} />,
            }]
        },

        {
            label: "False Positive",
            accessor: "id",
            canSort: false,
            cell: (props: any) => {
                if (hasPermission(["HRA-R"])) {
                    return <i className="fa-solid fa-thumbs-downcursor-pointeractive_the_btn delete_btn_center_align text-highlight-0 "
                        onClick={(e) => {
                            setHighRiskAccountId(props.row.original.id);
                            setShowFalsePositiveConfirm(true);
                            setTaxAccountNumber(props.row.original.taxAccountNumber)
                            setCollectorId(props.row.original.collectorId)
                            setNote(props.row.original.note)
                        }} />;
                } else {
                    return <>
                    </>
                }
            }
        },
        {
            label: "Promote to Linked Account",
            accessor: "importVersionId",
            canSort: false,
            cell: (props: any) => {
                if (hasPermission(["HRA-R"])) {
                    return <span className="fa-stack position-relative" onClick={(e) => {
                        setHighRiskAccountObj(props.row.original);
                        setShowAddLinkedAccountModal(true);

                    }}>
                        <i className="fas fa-link text-highlight-0 text-lg" />
                        <span className="absolute right-0  -top-1.5 flex items-center justify-center w-3.5 h-3.5 bg-text-5 rounded-full shadow-md">
                            <i className="fas fa-plus text-[10px] text-text-0"></i>
                        </span>
                    </span>
                } else {
                    return <>
                    </>
                }
            }
        },
    ]


    return (
        <>
            <div className="user-container px-4 py-2">
                <div className="flex justify-between mb-2.5">
                    <Breadcrumb crumbs={crumbs} />
                </div>

                <DataTable
                    columns={columns}
                    actions={{
                        fetchAction: useSearchHighRiskAccountsQuery,
                    }}
                    userSettingGroup={"High Risk Account"}
                    defaultFilter={{
                        "sort": "creationDate",
                        "sortOrder": "ASCENDING"
                    }}
                    defaultSortOptions={{
                        accessor: "creationDate",
                        direction: "asc"
                    }}

                />

            </div>

            {showAddLinkedAccountModal && (
                <AddLinkedAccountModal
                    show={showAddLinkedAccountModal}
                    setShow={setShowAddLinkedAccountModal}
                    highRishObj={highRiskAccountObj}
                />
            )}
            {showFalsePositiveConfirm && (
                <MarkFalsePositiveModal
                    show={true}
                    setShow={() => { }}
                    title={"Confirm False Positive"}
                    bodyMessage={"Are you sure you want to mark this account as false positive?"}
                    isLoading={false}
                    note={note} // Passing note state
                    setNote={setNote} // Passing setNote function
                    onSubmit={() => handleHighRiskAccountFalsePositive(note)}

                />
            )}

        </>
    )
}
export default HighRiskAccount